import { useEffect, useRef, useState } from "react";
import { axiosInstance } from "@/contexts/axiosApiInstance";
import type { UnitDetails } from "./types";

// Per-quantity-kind cache for the open picker. Keys are quantity-kind URIs
// (or "" for "no filter"). Common-units details are static within a session
// — the catalog only changes across page reloads via the post-deploy gate.
const TIMEOUT_MS = 8000;

export interface CommonUnitsState {
  loading: boolean;
  error: string | null;
  units: UnitDetails[];
}

export function useCommonUnits(
  quantityKindUri: string | null | undefined,
): CommonUnitsState {
  const [state, setState] = useState<CommonUnitsState>({
    loading: true,
    error: null,
    units: [],
  });
  const cacheRef = useRef<Map<string, UnitDetails[]>>(new Map());

  useEffect(() => {
    const cacheKey = quantityKindUri ?? "";
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setState({ loading: false, error: null, units: cached });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    axiosInstance
      .get<UnitDetails[]>("/api/units/common", {
        params: quantityKindUri ? { quantity_kind: quantityKindUri } : {},
        signal: controller.signal,
        timeout: TIMEOUT_MS,
      })
      .then((res) => {
        if (cancelled) return;
        cacheRef.current.set(cacheKey, res.data);
        setState({ loading: false, error: null, units: res.data });
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return;
        const isTimeout =
          err?.code === "ECONNABORTED" || err?.message?.includes("timeout");
        setState({
          loading: false,
          error: isTimeout
            ? "Loading timed out"
            : "Could not load common units",
          units: [],
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [quantityKindUri]);

  return state;
}
